import React from 'react';
import {CircularProgress, Dialog, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {DialogProps} from '@mui/material/Dialog';
import {tid} from '../../../testUtils';

export type DialogProgressProps = DialogProps & {
  title?: string;
  text?: string;
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Following HEM project security patterns
 */
const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  // Remove dangerous elements and attributes
  const sanitized = html
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
    .replace(/<object[^>]*>.*?<\/object>/gi, '')
    .replace(/<embed[^>]*>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    .replace(/<meta[^>]*>/gi, '');

  return sanitized;
};

/**
 * Detects if text contains HTML markup
 */
const containsHtml = (text: string): boolean => {
  return /<[^>]+>/.test(text);
};

const DialogProgress: React.FC<DialogProgressProps> = (props) => {
  const {title, text, ...dialogProps} = props;

  const renderText = () => {
    if (!text) return null;

    // Automatically detect and safely render HTML content
    if (containsHtml(text)) {
      const sanitizedHtml = sanitizeHtml(text);
      return <span dangerouslySetInnerHTML={{__html: sanitizedHtml}} />;
    }

    // Plain text - no HTML processing needed
    return text;
  };

  return (
    <Dialog {...dialogProps} {...tid('dialog-progress')}>
      {title && <DialogTitle {...tid('dialog-title')}>{title}</DialogTitle>}
      <DialogContent style={{textAlign: 'center'}} {...tid('dialog-content')}>
        <CircularProgress {...tid('progress-spinner')} />
        {text && (
          <DialogContentText style={{overflowWrap: 'break-word'}} {...tid('dialog-text')}>
            {renderText()}
          </DialogContentText>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DialogProgress;
